import posthog from "posthog-js";

const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const host =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

let enabled = false;

/**
 * Initialize PostHog when a project key is configured. Safe to call once at app startup.
 */
export function initPostHog(): void {
  if (enabled || !key) {
    if (import.meta.env.DEV && !key) {
      console.info("[PostHog] No VITE_PUBLIC_POSTHOG_KEY — skipping initialization.");
    }
    return;
  }

  posthog.init(key, {
    api_host: host,

    // Modern defaults bundle (2026-01-30): history-change pageviews, strict replay
    // minimum duration, rageclick content ignorelist, head-injected external scripts.
    defaults: "2026-01-30",

    person_profiles: "identified_only",

    // $pageview on every React Router navigation — no manual route listener needed.
    capture_pageview: "history_change",
    capture_pageleave: true,

    // Autocapture — clicks, rageclicks, dead clicks.
    autocapture: true,
    rageclick: true,
    capture_dead_clicks: true,

    // Heatmaps (capture_heatmaps is checked before the legacy enable_heatmaps).
    capture_heatmaps: true,

    // Web vitals + network performance.
    capture_performance: {
      network_timing: true,
      web_vitals: true,
      web_vitals_allowed_metrics: ["LCP", "CLS", "FCP", "INP"],
    },

    // Exception autocapture — unhandled errors + promise rejections.
    // capture_console_errors off: Sentry already owns console error capture.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },

    // Session recording — balanced masking: input values masked, UI text/layout visible.
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        text: true,
        textarea: true,
        select: true,
      },
      // Opt-in selector to hard-mask specific elements when needed.
      maskTextSelector: "[data-ph-mask], .ph-mask",
    },
    // Stream console logs into the replay timeline (top-level key, not under session_recording).
    enable_recording_console_log: true,
  });
  enabled = true;
}

export function isPostHogEnabled(): boolean {
  return enabled;
}

export { posthog };
